import { Alert, message, notification } from 'antd';

import {
	Input,
	Button as BaseButton,
	Textarea,
	TimeRangeScheduler,
	TimeSlot,
	Combobox,
	ComboboxInput,
	ComboboxContent,
	ComboboxList,
	ComboboxItem,
	ComboboxEmpty,
	Tooltip,
	Checkbox,
	DataGrid,
} from '@hydrodigital/design-system';
import type { DataGridColumn } from '@hydrodigital/design-system';
import type { ComboboxOption } from '@hydrodigital/combobox';

import React, { useEffect, useMemo, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import DOMPurify from 'dompurify';
import { StarOutlined, DeleteOutlined, InfoCircleOutlined } from '@ant-design/icons';
import useInfrastructure from '../../hooks/useInfrastructure';
import useParameter from '../../hooks/useParameter';
import useLocation from '../../hooks/useLocation';
import useUser from '../../hooks/useUser';
import AlertService from '../../domain/Alerts/AlertService';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert as AlertType } from '@/domain/Alerts/Alert';
import {
	timeSlotsToAlertSchedule,
	alertScheduleToTimeSlots,
	AlertSchedule,
} from '@/utils/alertScheduleUtils';
import AlertMessageTextarea from './AlertMessageTextarea';

// Temporary type extension until @hydrodigital/design-system updates ButtonProps to include 'type'
const Button = BaseButton as React.ForwardRefExoticComponent<
	React.ComponentPropsWithoutRef<typeof BaseButton> & {
		type?: 'button' | 'submit' | 'reset';
	} & React.RefAttributes<HTMLButtonElement>
>;

const sanitizeMessage = (val: string): string => {
	return DOMPurify.sanitize(val, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });
};

const alertFormSchema = z
	.object({
		alertName: z.string().min(1, 'This field is required'),
		alertDescription: z.string().min(1, 'This field is required'),
		sendAtStart: z.boolean().default(false),
		sendAtFinal: z.boolean().default(false),
		alertUseForecast: z.boolean().default(false),
		alertStartMessage: z
			.string()
			.max(1000, 'Message must be 1000 characters or less')
			.default('')
			.transform(sanitizeMessage),
		alertFinalMessage: z
			.string()
			.max(1000, 'Message must be 1000 characters or less')
			.default('')
			.transform(sanitizeMessage),
	})
	.refine((data) => data.sendAtStart || data.sendAtFinal, {
		message: 'You must select at least one notification option.',
		path: ['sendAtStart'],
	});

type AlertFormData = z.input<typeof alertFormSchema>;

const alertService = new AlertService();

export default function AlertForm() {
	const navigate = useNavigate();
	const security = localStorage.getItem('security') || '{}';
	const userId = localStorage.getItem('userId') || '';
	const organizationId = JSON.parse(security).organizationId;
	const { alertId } = useParams();
	const { locations } = useLocation({ mode: 'ByUser', userId });
	const [selectedLocation, setSelectedLocation] = useState<number | null>(null);
	const [selectRecipient, setSelectRecipient] = useState<any>(null);
	const [newRule, setNewRule] = useState<any>({
		selectedInfrastructure: null,
		selectedParam: null,
		criticalLow: null,
		criticalHigh: null,
	});
	// const [selectedDates, setselectedDates] = useState<any>([]);
	const [valueSelectDates, setValueSelectDates] = useState<TimeSlot[]>([]);
	const [rules, setRules] = useState<any[]>([]);
	const [recipients, setRecipients] = useState<any[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const {
		control,
		handleSubmit,
		setValue,
		getValues,
		watch,
		trigger,
		reset,
		formState: { errors },
	} = useForm<AlertFormData>({
		resolver: zodResolver(alertFormSchema),
		defaultValues: {
			alertName: '',
			alertDescription: '',
			sendAtStart: false,
			sendAtFinal: false,
			alertUseForecast: false,
			alertStartMessage: '',
			alertFinalMessage: '',
		},
	});
	const [infrastructureValue, setInfrastructureValue] = useState<string | undefined>(undefined);
	const [parameterValue, setParameterValue] = useState<string | undefined>(undefined);
	const [userValue, setUserValue] = useState<string | undefined>(undefined);

	const { infrastructures, setParams: setInfraParams } = useInfrastructure({
		mode: 'ByLocation',
		location: selectedLocation,
		organizationId: null,
		size: 50,
	});

	const { parameters } = useParameter({
		infrastructureId: newRule.selectedInfrastructure?.value
			? Number(newRule.selectedInfrastructure.value)
			: null,
	});

	useEffect(() => {
		setInfrastructureValue(undefined);
		setParameterValue(undefined);
		setNewRule((prev: any) => ({
			...prev,
			selectedInfrastructure: null,
			selectedParam: null,
		}));
		setInfraParams((prev: any) => {
			if (prev.location !== selectedLocation) {
				return { ...prev, location: selectedLocation };
			}
			return prev;
		});
	}, [selectedLocation]);

	const infrastructureItems: ComboboxOption[] = useMemo(
		() =>
			(infrastructures || []).map((i: any) => ({
				value: String(i.id),
				label: i.name,
			})),
		[infrastructures],
	);

	const filteredParameterIds = rules.map((rl) =>
		rl.selectedParam ? rl.selectedParam.id : rl.parameterId,
	);
	const parameterItems: ComboboxOption[] = useMemo(
		() =>
			(parameters || [])
				.filter((p: any) => !filteredParameterIds.includes(p.id))
				.map((p: any) => ({
					value: String(p.id),
					label: p.name,
				})),
		[parameters, filteredParameterIds],
	);

	const { users, setParams: setUserParams } = useUser({
		organizationId,
	});

	const userItems: ComboboxOption[] = useMemo(
		() =>
			(users || []).map((u: any) => ({
				value: u.userId,
				label: `${u.firstName} ${u.lastName}`,
			})),
		[users],
	);

	function mapLocationsToOptions(nodes: any[]): ComboboxOption[] {
		return nodes.map((loc) => {
			const option: ComboboxOption = {
				value: String(loc.locationId),
				label: String(loc.locationName || ''),
				...(loc.canShowLocation ? {} : { disabled: true }),
			};
			if (loc.hasChild && Array.isArray(loc.childLocations) && loc.childLocations.length > 0) {
				(option as any).children = mapLocationsToOptions(loc.childLocations);
			}
			return option;
		});
	}

	const locationOptions = useMemo(() => {
		if (!locations || locations.length === 0) return [];
		return mapLocationsToOptions(locations);
	}, [locations]);

	const columnsRules: DataGridColumn<any>[] = [
		{
			id: 'infrastructure',
			accessorKey: 'infrastructureName',
			header: 'Asset',
			cell: ({ row }) => {
				const { selectedInfrastructure, infrastructureName } = row.original;
				return <>{selectedInfrastructure ? selectedInfrastructure.label : infrastructureName}</>;
			},
		},
		{
			id: 'parameter',
			accessorKey: 'parameterName',
			header: 'Parameter',
			cell: ({ row }) => {
				const { selectedParam, parameterName } = row.original;
				return <>{selectedParam ? selectedParam.name : parameterName}</>;
			},
		},
		{
			id: 'criticalValues',
			accessorKey: 'criticalLow',
			header: 'Threshold Values',
			cell: ({ row }) => {
				const { criticalLow, criticalHigh } = row.original;
				return (
					<>
						{criticalLow} - {criticalHigh}
					</>
				);
			},
		},
		{
			id: 'principal',
			accessorKey: 'mainParameter',
			header: 'Main Parameter',
			cell: ({ row }) => {
				const { mainParameter, selectedParam } = row.original;
				return (
					<StarOutlined
						style={{
							color: mainParameter ? '#1677ff' : '#C4CCD5',
							cursor: 'pointer',
						}}
						onClick={() => {
							handleUpdateMainRule(selectedParam.id);
						}}
					/>
				);
			},
		},
		{
			id: 'action',
			accessorKey: 'selectedParam',
			header: 'Action',
			cell: ({ row }) => {
				const { selectedParam } = row.original;
				return (
					<Tooltip title="Delete">
						<DeleteOutlined
							style={{ fontSize: '1.5rem' }}
							onClick={() => {
								setRules((rules) => {
									return rules.filter((rp) => rp.selectedParam.id != selectedParam.id);
								});
							}}
						/>
					</Tooltip>
				);
			},
		},
	];

	const columnsUsers: DataGridColumn<any>[] = [
		{
			id: 'username',
			accessorKey: 'name',
			header: 'Username',
			cell: ({ row }) => {
				const record = row.original;
				const name =
					record.name ||
					[record.firstName, record.lastName].filter(Boolean).join(' ') ||
					record.user?.name ||
					'—';

				const email = record.email ?? record.user?.email ?? '';

				const phone =
					[record.phoneNumber, record.phone, record.user?.phoneNumber, record.user?.phone]
						.map((v: any) => (typeof v === 'string' ? v.trim() : v))
						.find(Boolean) ?? '';

				return (
					<div>
						<div>{name}</div>
						{email && <div className="text-gray-500 text-xs">{email}</div>}
						{phone && <div className="text-gray-500 text-xs">{phone}</div>}
					</div>
				);
			},
		},
		{
			id: 'channels',
			accessorKey: 'enableSendEmail',
			header: 'External Notification Channels',
			cell: ({ row }) => {
				const { userId, enableSendEmail, enableSendSms } = row.original;
				return (
					<div className="flex">
						<Checkbox
							checked={enableSendEmail}
							className="mr-2"
							label="Email"
							size='small'
							onChecked={(checked) => {
								const copy = [...recipients];
								const finded = copy.findIndex((recipient: any) => {
									return recipient.userId == userId;
								});
								copy[finded].enableSendEmail = checked;
								setRecipients(copy);
							}}
						/>
						<Checkbox
							checked={enableSendSms}
							label="SMS"
							size='small'                                                                
							onChecked={(checked) => {
								const copy = [...recipients];
								const finded = copy.findIndex((recipient: any) => {
									return recipient.userId == userId;
								});
								copy[finded].enableSendSms = checked;
								setRecipients(copy);
							}}
						/>
					</div>
				);
			},
		},
		{
			id: 'action',
			accessorKey: 'userId',
			header: 'Action',
			cell: ({ row }) => {
				const { userId } = row.original;
				return (
					<Tooltip title="Delete">
						<DeleteOutlined
							style={{ fontSize: '1.5rem' }}
							onClick={() => {
								setRecipients(() => {
									return recipients.filter((rp) => rp.userId != userId);
								});
							}}
						/>
					</Tooltip>
				);
			},
		},
	];

	function completarHoras(fechas: any[]) {
		const fechasCompletas: Date[] = [];
		const fechasPorDia: any = {};

		fechas.forEach((fecha: any) => {
			const diaClave = `${new Date(fecha).getDay()}`;
			if (!fechasPorDia[diaClave]) {
				fechasPorDia[diaClave] = [];
			}
			fechasPorDia[diaClave].push(new Date(fecha));
		});

		Object.keys(fechasPorDia).forEach((dia) => {
			const fechasDelDia = fechasPorDia[dia];
			const fechaFinDelDia = new Date(Math.max.apply(null, fechasDelDia));
			const fechaInicioDelDia = new Date(Math.min.apply(null, fechasDelDia));

			while (fechaInicioDelDia <= fechaFinDelDia) {
				fechasCompletas.push(new Date(fechaInicioDelDia));
				fechaInicioDelDia.setHours(fechaInicioDelDia.getHours() + 1);
			}
		});

		return fechasCompletas;
	}

	function getParsedHours(dates: any) {
		const fechasPorDia: any = {};
		const datesParsed: any = [];

		// Agrupar las fechas por día
		dates.forEach((fecha: any) => {
			const diaClave = `${new Date(fecha).getDay()}`;
			if (!fechasPorDia[diaClave]) {
				fechasPorDia[diaClave] = [];
			}
			fechasPorDia[diaClave].push(new Date(fecha));
		});
		Object.keys(fechasPorDia).forEach((dia) => {
			const fechasDelDia = fechasPorDia[dia];
			const minValue = new Date(Math.min.apply(null, fechasDelDia));
			const maxValue = new Date(Math.max.apply(null, fechasDelDia));

			datesParsed.push({
				day: minValue.getDay() === 0 ? 7 : minValue.getDay(),
				startTime: minValue.getHours(),
				endTime: maxValue.getHours(),
			});
		});
		return datesParsed;
	}

	function getOriginalDates(datesParsed: any) {
		const dates: Date[] = [];
		const baseDate = new Date('2020-09-14T00:00:00Z');

		datesParsed.forEach((parsedDate: { day: number; startTime: number; endTime: number }) => {
			const startDate = new Date(baseDate);
			startDate.setDate(baseDate.getDate() + parsedDate.day);
			startDate.setHours(parsedDate.startTime, 0, 0, 0);

			const endDate = new Date(baseDate);
			endDate.setDate(baseDate.getDate() + parsedDate.day);
			endDate.setHours(parsedDate.endTime, 0, 0, 0);

			dates.push(startDate, endDate);
		});
		return dates;
	}

	async function handleUpdateMainRule(parameterId: string | number) {
		setRules((rules: any) => {
			return rules.map((rule: any) => {
				return { ...rule, mainParameter: rule.selectedParam.id == parameterId };
			});
		});
	}

	async function handleGetAlertById(alertId: number) {
		const res = await alertService.getAlertById(alertId);
		return res;
	}

	const handleChange2 = (val: TimeSlot[]) => {
		setValueSelectDates(val);
	};

	async function onFormSubmit(values: AlertFormData) {
		let errorCount = 0;
		if (rules.length <= 0) {
			message.error('You must add at least one rule');
			errorCount++;
		} else {
			if (rules.findIndex((rl) => rl.mainParameter) < 0) {
				message.error('You must select the main rule');
				errorCount++;
			}
		}

		if (recipients.length <= 0) {
			message.error('You must add at least one recipient');
			errorCount++;
		}
		if (valueSelectDates.length <= 0) {
			message.error('You must select at least one date for alert');
			errorCount++;
		}

		if (errorCount > 0) return;
		const organizationId = JSON.parse(localStorage.getItem('security') || '{}').organizationId;

		const newAlert: AlertType = {
			organizationId,
			alertName: values.alertName,
			alertDescription: values.alertDescription,
			alertSendStart: !!values.sendAtStart,
			alertSendFinal: !!values.sendAtFinal,
			alertStartMessage: values.alertStartMessage ?? '',
			alertFinalMessage: values.alertFinalMessage ?? '',
			alertEnable: true,
			alertWhenFiring: timeSlotsToAlertSchedule(valueSelectDates),
			rules: rules.map((rule) => {
				return {
					parameterId: rule.selectedParam.id,
					criticalLow: rule.criticalLow,
					criticalHigh: rule.criticalHigh,
					mainParameter: rule.mainParameter,
				};
			}),
			recipients: recipients.map((recipient) => {
				return {
					...(alertId && recipient.recipientId ? { recipientId: recipient.recipientId } : {}),
					userId: recipient.userId || recipient.id,
					enableSendEmail: recipient.enableSendEmail,
					enableSendSms: recipient.enableSendSms,
				};
			}),
			alertUseForecast: !!values.alertUseForecast,
		};
		if (!alertId) {
			const res = await alertService.createAlert(newAlert);
			if (res.success) {
				notification.success({
					message: 'Successful',
					description: 'Alert created successfully',
				});
				navigate(`/alerts`);
			}
		} else {
			const res = await alertService.updateAlert(parseInt(alertId), newAlert);
			if (res.success) {
				notification.success({
					message: 'Successful',
					description: 'Alert updated successfully',
				});
				navigate(`/alerts`);
			}
		}
	}

	function onFormError() {
		if (rules.length <= 0) {
			message.error('You must add at least one rule');
		}
		if (recipients.length <= 0) {
			message.error('You must add at least one recipient');
		}
		message.error('Verify all fields');
	}

	useEffect(() => {
		if (alertId) {
			handleGetAlertById(parseInt(alertId)).then((alert: AlertType) => {
				const rawRecipients: any[] = Array.isArray((alert as any).recipientsDTO)
					? (alert as any).recipientsDTO
					: Array.isArray((alert as any).recipients)
						? (alert as any).recipients
						: [];
				const normalizedRecipients = rawRecipients.map((r: any) => {
					const phone =
						[r.phoneNumber, r.phone, r.mobile, r.msisdn, r.user?.phoneNumber, r.user?.phone]
							.map((v) => (typeof v === 'string' ? v.trim() : ''))
							.find((v) => v) || '';

					return {
						recipientId: r.recipientId ?? r.recipientsId ?? r.id,
						userId: r.userId ?? r.id ?? r.user?.id,
						name:
							r.name ??
							r.userNames ??
							[r.firstName, r.lastName].filter(Boolean).join(' ') ??
							r.user?.name ??
							'',
						email: (r.email ?? r.userMail ?? r.user?.email ?? '').trim(),
						phoneNumber: phone,
						enableSendEmail: r.enableSendEmail ?? r.enableEmail ?? true,
						enableSendSms: r.enableSendSms ?? r.enableSms ?? true,
					};
				});

				setValue('alertName', alert.alertName);
				setValue('alertDescription', alert.alertDescription);
				setValue('sendAtStart', alert.alertSendStart);
				setValue('sendAtFinal', alert.alertSendFinal);
				setValue('alertUseForecast', alert.alertUseForecast);
				setValue('alertStartMessage', alert.alertStartMessage ?? '');
				setValue('alertFinalMessage', alert.alertFinalMessage ?? '');

				setRules(
					alert.rules.map((rule) => ({
						...rule,
						selectedParam: { name: rule.parameterName, id: rule.parameterId },
					})),
				);
				setValueSelectDates(
					alertScheduleToTimeSlots(
						Array.isArray(alert.alertWhenFiring)
							? (structuredClone(alert.alertWhenFiring) as AlertSchedule[])
							: [],
					),
				);

				setRecipients(normalizedRecipients);
				setIsLoading(false);
			});
		} else {
			setIsLoading(false);
		}
	}, []);

	return (
		!isLoading && (
			<div
				className="flex flex-col px-4"
				style={{ overflow: 'auto', height: 'calc(100vh - 65px)' }}
			>
				<form onSubmit={handleSubmit(onFormSubmit, onFormError)} autoComplete="off">
					<div className="flex flex-col">
						<span>
							Alert Name <span style={{ color: 'red' }}>*</span>
						</span>
						<Controller
							name="alertName"
							control={control}
							render={({ field }) => (
								<Input
									{...field}
									size="small"
									className="text-title-page"
									placeholder="Enter an Alert Name"
								/>
							)}
						/>
						{errors.alertName && (
							<span style={{ color: '#ff4d4f', fontSize: 12 }}>{errors.alertName.message}</span>
						)}
						<span className="mt-2">
							Alert Description <span style={{ color: 'red' }}>*</span>
						</span>
						<Controller
							name="alertDescription"
							control={control}
							render={({ field }) => (
								<Textarea
									{...field}
									placeholder="Enter a description"
									minRows={3}
									maxRows={5}
									resize="none"
								/>
							)}
						/>
						{errors.alertDescription && (
							<span style={{ color: '#ff4d4f', fontSize: 12 }}>
								{errors.alertDescription.message}
							</span>
						)}
						<div className="mt-4 flex w-100 items-start">
							<div style={{ minWidth: '310px' }}>
								<Controller
									name="sendAtStart"
									control={control}
									render={({ field }) => (
										<Checkbox
											checked={field.value}
											label="Send notification when alert starts"
											onChecked={(checked) => {
												field.onChange(checked);
												trigger('sendAtStart');
											}}
										/>
									)}
								/>
								{errors.sendAtStart && (
									<div style={{ color: '#ff4d4f', fontSize: 12 }}>{errors.sendAtStart.message}</div>
								)}
							</div>
						</div>
						<div className="mt-2 flex w-100 items-start">
							<div style={{ minWidth: '310px' }}>
								<Controller
									name="sendAtFinal"
									control={control}
									render={({ field }) => (
										<Checkbox
											checked={field.value}
											label="Send notification when alert clears"
											onChecked={(checked) => {
												field.onChange(checked);
												trigger('sendAtStart');
											}}
										/>
									)}
								/>
							</div>
						</div>
						<div className="mt-2">
							<Controller
								name="alertUseForecast"
								control={control}
								render={({ field }) => (
									<Checkbox
										checked={field.value}
										label="Also notify for forecasted occurrences"
										onChecked={(checked) => field.onChange(checked)}
									/>
								)}
							/>
						</div>
						<div className="mt-4 bg-blue-50 border border-blue-200 rounded-md px-4 py-3 text-sm leading-relaxed text-gray-700">
							<div className="flex items-center gap-1.5 mb-2">
								<InfoCircleOutlined className="text-blue-500 text-base" />
								<strong className="text-sm">Custom Alert Messages</strong>
							</div>
							<p className="mb-2">
								Set a custom message to be sent when the alert starts or clears. Type <code>$</code>{' '}
								to insert variables that will be replaced with actual values when the alert
								triggers.
							</p>
							<div className="grid grid-cols-2 gap-4">
								<div>
									<strong>Start Message Example:</strong>
									<pre className="mt-1 bg-white border border-gray-300 rounded p-2 text-xs whitespace-pre-wrap leading-normal">
										{`🚨 CRITICAL ALERT

$MainParameter HIGH

Asset: $AssetName
Location: $LocationName

$MainParameter: $MainValue $MainUnits (>$MainThresholdHi $MainUnits)
$AuxParameterN: $AuxValueN $AuxUnitsN (>$AuxThresholdNHi $AuxUnitsN)`}
									</pre>
								</div>
								<div>
									<strong>End Message Example:</strong>
									<pre className="mt-1 bg-white border border-gray-300 rounded p-2 text-xs whitespace-pre-wrap leading-normal">
										{`✅ ALERT CLEARED

$MainParameter returned to normal

Asset: $AssetName
Location: $LocationName

$MainParameter: $MainValue $MainUnits
Time of alert: $TimeofAlert`}
									</pre>
								</div>
							</div>
						</div>
						<div className="mt-4 grid grid-cols-2 gap-4">
							<Controller
								name="alertStartMessage"
								control={control}
								render={({ field }) => (
									<AlertMessageTextarea
										value={field.value ?? ''}
										onChange={field.onChange}
										label="Alert Start Message"
										placeholder="Custom message when alert starts. Type $ to insert variables..."
										errorMessage={errors.alertStartMessage?.message}
									/>
								)}
							/>
							<Controller
								name="alertFinalMessage"
								control={control}
								render={({ field }) => (
									<AlertMessageTextarea
										value={field.value ?? ''}
										onChange={field.onChange}
										label="Alert End Message"
										placeholder="Custom message when alert clears. Type $ to insert variables..."
										errorMessage={errors.alertFinalMessage?.message}
									/>
								)}
							/>
						</div>
						<span className="mt-4 f-18" style={{ fontWeight: 'bold' }}>
							Rules
						</span>
						{rules.length <= 0 && <Alert message="You need add at least one rule" type="error" />}
						<div
							className="mt-2 items-center"
							style={{
								display: 'grid',
								gridTemplateColumns: '1fr 1fr 1fr',
								gridColumnGap: '20px',
							}}
						>
							<div>
								<span style={{ width: '150px' }}>
									Location <span style={{ color: 'red' }}>*</span>
								</span>
								<Combobox
									items={locationOptions}
									onValueChange={(value) => {
										const locationId = Number(value);
										setSelectedLocation(locationId);
										setNewRule((prev: any) => ({
											...prev,
											selectedInfrastructure: null,
											selectedParam: null,
										}));
										setInfrastructureValue(undefined);
										setParameterValue(undefined);
									}}
									onClear={() => {
										setSelectedLocation(null);
										setNewRule((prev: any) => ({
											...prev,
											selectedInfrastructure: null,
											selectedParam: null,
										}));
										setInfrastructureValue(undefined);
										setParameterValue(undefined);
									}}
									size="small"
								>
									<ComboboxInput placeholder="Select Location" />
									<ComboboxContent>
										<ComboboxEmpty>No locations found.</ComboboxEmpty>
										<ComboboxList>
											{(item) => (
												<ComboboxItem key={item.value} value={item}>
													{item.label}
												</ComboboxItem>
											)}
										</ComboboxList>
									</ComboboxContent>
								</Combobox>
							</div>
							<div>
								<span style={{ width: '150px' }}>
									Asset <span style={{ color: 'red' }}>*</span>
								</span>
								<Combobox
									key={`asset-${selectedLocation}`}
									items={infrastructureItems}
									value={infrastructureValue}
									onValueChange={(val) => {
										setInfrastructureValue(val);
										const infra = infrastructures.find((i: any) => String(i.id) === val);
										setNewRule((rl: any) => ({
											...rl,
											selectedInfrastructure: infra ? { value: infra.id, label: infra.name } : null,
											selectedParam: null,
										}));
										setParameterValue(undefined);
									}}
									onClear={() => {
										setInfrastructureValue(undefined);
										setNewRule((prev: any) => ({
											...prev,
											selectedInfrastructure: null,
											selectedParam: null,
										}));
										setParameterValue(undefined);
									}}
									size="small"
								>
									<ComboboxInput placeholder="Select Asset" />
									<ComboboxContent>
										<ComboboxEmpty>No assets found.</ComboboxEmpty>
										<ComboboxList>
											{(item) => (
												<ComboboxItem key={item.value} value={item}>
													{item.label}
												</ComboboxItem>
											)}
										</ComboboxList>
									</ComboboxContent>
								</Combobox>
							</div>
							<div>
								<span style={{ width: '150px' }}>
									Parameter <span style={{ color: 'red' }}>*</span>
								</span>
								<Combobox
									key={`param-${selectedLocation}-${infrastructureValue}`}
									items={parameterItems}
									value={parameterValue}
									onValueChange={(val) => {
										setParameterValue(val);
										const param = parameters.find((p: any) => String(p.id) === val);
										setNewRule((rl: any) => ({
											...rl,
											selectedParam: param || null,
										}));
									}}
									size="small"
								>
									<ComboboxInput placeholder="Select Parameter" />
									<ComboboxContent>
										<ComboboxEmpty>No parameters found.</ComboboxEmpty>
										<ComboboxList>
											{(item) => (
												<ComboboxItem key={item.value} value={item}>
													{item.label}
												</ComboboxItem>
											)}
										</ComboboxList>
									</ComboboxContent>
								</Combobox>
							</div>
						</div>
						<div
							className="mt-2"
							style={{
								display: 'grid',
								gridTemplateColumns: '1fr 1fr 1fr',
								gridColumnGap: '20px',
							}}
						>
							<div>
								<span style={{ width: '150px' }}>
									Low Threshold <span style={{ color: 'red' }}>*</span>
								</span>
								<br />
								<Input
									size="small"
									type="number"
									value={newRule.criticalLow ?? ''}
									onChange={(e) => {
										const raw = e?.target?.value ?? '';
										const num = raw === '' ? null : Number(raw);
										if (raw !== '' && Number.isNaN(num)) {
											// Ignore invalid intermediate input
											return;
										}
										if (num !== null && num < 0) {
											message.error('Value must be at least 0');
											return;
										}
										setNewRule((rl: any) => ({ ...rl, criticalLow: num }));
									}}
									min={0}
									step={0.1}
								/>
							</div>
							<div>
								<span style={{ width: '150px' }}>
									High Threshold <span style={{ color: 'red' }}>*</span>
								</span>
								<br />
								<Input
									size="small"
									type="number"
									value={newRule.criticalHigh ?? ''}
									onChange={(e) => {
										const raw = e?.target?.value ?? '';
										const num = raw === '' ? null : Number(raw);
										if (raw !== '' && Number.isNaN(num)) {
											// Ignore invalid intermediate input
											return;
										}
										if (num !== null && num <= 0) {
											message.error('Value must be at least 0.01');
											return;
										}
										setNewRule((rl: any) => ({ ...rl, criticalHigh: num }));
									}}
									min={0.01}
									step={0.01}
								/>
							</div>
							<div style={{ placeSelf: 'center' }}>
								{/* <Button
                  shape="round"
                  icon={<PlusCircleOutlined />}
                  className="flex items-center self-center"
                  style={{
                    backgroundColor: "#023E65",
                    color: "white",
                    width: "auto",
                    height: "40px",
                  }}
                  disabled={
                    !newRule.selectedParam ||
                    newRule.criticalLow === null ||
                    newRule.criticalHigh === null
                  }
                  onClick={() => {
                    if (
                      !newRule.selectedParam ||
                      newRule.criticalLow === null ||
                      newRule.criticalHigh === null
                    ) {
                      message.error("Complete all fields");
                      return;
                    } else if (
                      rules.findIndex(
                        (rule) =>
                          rule.selectedParam.id == newRule.selectedParam.id
                      ) >= 0
                    ) {
                      message.error(
                        "You already have a rule for this parameter"
                      );
                      return;
                    } else if (newRule.criticalLow >= newRule.criticalHigh) {
                      message.error(
                        "Critital High must be greater than Critical Low"
                      );
                      return;
                    }
                    setRules((rules) => {
                      return [
                        ...rules,
                        {
                          ...newRule,
                          mainParameter: !(rules.length > 0),
                        },
                      ];
                    });
                    setNewRule({
                      ...newRule,
                      selectedParam: null,
                      criticalLow: null,
                      criticalHigh: null,
                      mainParameter: false,
                    });
                    selectParameter?.current?.cleanValue();
                    selectInfrastructureRef.current.setValue(null);
                  }}
                >
                  Add rule
                </Button> */}

								<Button
									variant="primary"
									size="small"
									label="Add rule"
									iconName="plus-circle"
									iconColor="white"
									className="mt-2"
									style={{
										backgroundColor: '#023E65',
										color: 'white',
										width: 'auto',
										height: '40px',
									}}
									disabled={
										!newRule.selectedParam ||
										newRule.criticalLow === null ||
										newRule.criticalHigh === null
									}
									onClick={() => {
										if (
											!newRule.selectedParam ||
											newRule.criticalLow === null ||
											newRule.criticalHigh === null
										) {
											message.error('Complete all fields');
											return;
										} else if (
											rules.findIndex(
												(rule) => rule.selectedParam.id == newRule.selectedParam.id,
											) >= 0
										) {
											message.error('You already have a rule for this parameter');
											return;
										} else if (newRule.criticalLow >= newRule.criticalHigh) {
											message.error('Critital High must be greater than Critical Low');
											return;
										}
										setRules((rules) => {
											return [
												...rules,
												{
													...newRule,
													mainParameter: !(rules.length > 0),
												},
											];
										});
										setNewRule({
											...newRule,
											selectedParam: null,
											criticalLow: null,
											criticalHigh: null,
											mainParameter: false,
										});
										setParameterValue(undefined);
										setInfrastructureValue(undefined);
									}}
								/>
							</div>
						</div>
						<div className="mt-2">
							<DataGrid
								columns={columnsRules}
								data={rules}
								pagination={{ enabled: false }}
								bordered
								size="compact"
								maxHeight="calc(100vh - 280px)"
							/>
						</div>
						<span className="mt-4 f-18" style={{ fontWeight: 'bold' }}>
							Firing Schedule
						</span>
						{valueSelectDates.length <= 0 && (
							<Alert className="mb-2" message="You need select the dates" type="error" />
						)}
						{/* <div className="">
							<ScheduleSelector
								selection={selectedDates}
								numDays={7}
								minTime={0}
								maxTime={24}
								timeFormat={'hh:mm A'}
								dateFormat={'ddd'}
								startDate={new Date('9-14-20')}
								onChange={handleChange}
							/>
						</div> */}
						<div>
							<TimeRangeScheduler
								value={valueSelectDates}
								onChange={handleChange2}
								startHour={0}
								endHour={23}
								showPresets={false}
								showTimezone={false}
								showTimezoneInfo={false}
								weekStartsOn={1}
							/>
						</div>
						<span className="mt-4 f-18" style={{ fontWeight: 'bold' }}>
							Recipients
						</span>
						{recipients.length <= 0 && (
							<Alert className="mb-2" message="You need add at least one recipient" type="error" />
						)}
						<div className="flex">
							<Combobox
								items={userItems}
								value={userValue}
								onValueChange={(val) => {
									setUserValue(val);
									const user = users.find((u: any) => u.userId === val);
									setSelectRecipient(user || null);
								}}
								onSearchChange={(val: string) => {
									if (val.length >= 3) {
										setUserParams((prev: any) => ({
											...prev,
											userName: val,
										}));
									}
								}}
								onClear={() => {
									setUserValue(undefined);
									setSelectRecipient(null);
									setUserParams((prev: any) => ({
										...prev,
										userName: null,
									}));
								}}
								size="small"
							>
								<ComboboxInput placeholder="User" />
								<ComboboxContent>
									<ComboboxEmpty>No users found.</ComboboxEmpty>
									<ComboboxList>
										{(item) => (
											<ComboboxItem key={item.value} value={item}>
												{item.label}
											</ComboboxItem>
										)}
									</ComboboxList>
								</ComboboxContent>
							</Combobox>
							{/* <Button
                shape="round"
                icon={<PlusCircleOutlined />}
                className="flex items-center ml-2"
                style={{
                  backgroundColor: "#023E65",
                  color: "white",
                  width: "auto",
                }}
                disabled={!selectRecipient}
                onClick={() => {
                  if (selectRecipient.userId) {
                    const newObj = {
                      userId: selectRecipient.userId,
                      name: `${selectRecipient.firstName} ${selectRecipient.lastName}`,
                      email: selectRecipient.email,
                      phoneNumber: selectRecipient.phoneNumber,
                      enableSendEmail: true,
                      enableSendSms: true,
                    };
                    if (!recipients.find((rp) => rp.userId == newObj.userId)) {
                      setRecipients((prev: any) => {
                        return [...prev, newObj];
                      });
                      setSelectRecipient(null);
                      selectUser.current.cleanValue();
                    }
                  }
                }}
              >
                Add recipient
              </Button> */}

							<Button
								variant="primary"
								size="small"
								label="Add recipient"
								iconName="plus-circle"
								iconColor="white"
								className="flex items-center ml-2"
								style={{
									backgroundColor: '#023E65',
									color: 'white',
									width: 'auto',
								}}
								disabled={!selectRecipient}
								onClick={() => {
									if (selectRecipient.userId) {
										const newObj = {
											userId: selectRecipient.userId,
											name: `${selectRecipient.firstName} ${selectRecipient.lastName}`,
											email: selectRecipient.email,
											phoneNumber: selectRecipient.phoneNumber,
											enableSendEmail: true,
											enableSendSms: true,
										};
										if (!recipients.find((rp) => rp.userId == newObj.userId)) {
											setRecipients((prev: any) => {
												return [...prev, newObj];
											});
											setSelectRecipient(null);
											setUserValue(undefined);
										}
									}
								}}
							/>
						</div>

						<div className="mt-2">
							<DataGrid
								columns={columnsUsers}
								data={recipients}
								pagination={{ enabled: false }}
								bordered
								size="compact"
								maxHeight="calc(100vh - 280px)"
							/>
						</div>
						{/* <Button
							className="my-2"
							style={{
								width: '85px',
								height: '40px',
								backgroundColor: '#00203C',
								color: 'white',
								alignSelf: 'center',
							}}
							htmlType="submit"
						>
							Accept
						</Button> */}

						<Button
							type="submit"
							label="Accept"
							showIcon={false}
							className="my-2"
							style={{
								width: '85px',
								height: '40px',
								backgroundColor: '#00203C',
								color: 'white',
								alignSelf: 'center',
							}}
						/>
					</div>
				</form>
			</div>
		)
	);
}
